/*
    공통 모달 -  Round N 생성 모달
*/

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/app/hooks";
import { listLibraryAPI } from "src/store/features/library/libraryListSlice";
import { libraryRoundUpdateAPI } from "src/store/features/library/libraryRoundUpdateSlice";

// Props. 다음 라운드 생성 대상과 생성 완료 콜백을 전달.
interface NextRoundMoveModalProps {
    open: boolean;
    onClose: () => void;
    selectedLibraryForNextRound: string | null;
    nextRoundNumber: number | null;
    onCreateSuccess?: () => void;
  }

  // 다음 라운드 이동 모달. 라운드 생성 조건을 보여주고 생성 후 해당 라운드로 이동.
  const NextRoundMoveModal = ({
    open,
    onClose,
    selectedLibraryForNextRound,
    nextRoundNumber,
    onCreateSuccess,
  }: NextRoundMoveModalProps) => {

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const listLibrary = useAppSelector((state) => state?.listLibrary?.response);
  const whyNeedNextRound = t("nextRound.modal.whyNeeded.limitations", {returnObjects: true,});
  const list = t(`nextRound.modal.conditions.list`, { returnObjects: true });

  // 워크플로우 경로 변환. 현재 URL 또는 workflow_name으로 라우트 prefix를 결정.
  const getWorkflowPath = (workflowName: string) => {
    if (location.pathname.includes("/kinase-inhibitor/")) return "kinase-inhibitor";
    if (location.pathname.includes("/ppi-inhibitor/")) return "ppi-inhibitor";
    if (location.pathname.includes("/drug-response/clinical/")) return "drug-response/clinical";
    if (location.pathname.includes("/drug-response/preclinical/")) return "drug-response/preclinical";

    const normalized = (workflowName || "").toLowerCase().trim();
    if (normalized.includes("kinase")) return "kinase-inhibitor";
    if (normalized.includes("ppi")) return "ppi-inhibitor";
    if (normalized.includes("drug") || normalized.includes("response")) {
      return "drug-response/preclinical";
    }
    return "ppi-inhibitor";
  };

  // 다음 라운드 생성. 라이브러리 라운드 업데이트 후 목록 갱신과 화면 이동을 수행.
  const handleCreateNextRound = async () => {
    const libraryInfo = listLibrary?.find(
      (lib) => lib.library_name === selectedLibraryForNextRound
    );

    if (libraryInfo) {
      try {
        // 1. libraryRoundUpdateAPI 호출
        await dispatch(
          libraryRoundUpdateAPI({
            group_name: "temp_group",
            library_name: selectedLibraryForNextRound,
            round_num: nextRoundNumber,
          })
        ).unwrap();

        // 2. listLibrary 갱신
        await dispatch(listLibraryAPI({ group_name: "temp_group" })).unwrap();

        // 3. 네비게이션
        const workflowPath = getWorkflowPath(libraryInfo.workflow_name);
        navigate(
          `/${workflowPath}/${selectedLibraryForNextRound}/round${nextRoundNumber}`
        );

      } catch (error) {
        console.error("Failed to create next round:", error);

      }
      
      onCreateSuccess?.();  
      onClose();
    }
  };

  return (
    <>
      {/* 다음 라운드 생성 확인 모달 */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxWidth: "900px",
            maxHeight: "90vh",
          },
        }}
      >
        {/* 헤더 섹션 */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg,rgb(230, 230, 230) 0%,rgb(234, 234, 234) 100%)",
            padding: "32px 40px",
            borderBottom: "1px solid rgb(226, 226, 227)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                bgcolor: "#2f5596",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
            >
              {nextRoundNumber}
            </Box>
            <Box>
              <DialogTitle
                sx={{ fontSize: "1.75rem", fontWeight: 700, p: 0, mb: 0.5 }}
              >
                Create Round {nextRoundNumber}
              </DialogTitle>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                {t(`nextRound.modal.subtitle`)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 컨텐츠 섹션 - 스크롤 가능 */}
        <DialogContent
          sx={{ padding: "32px 40px", maxHeight: "60vh", overflowY: "auto" }}
        >
          {/* Workspace 정보 카드 */}
          <Box
            sx={{
              bgcolor: "#f8fafc",
              border: "2px solid #e2e8f0",
              borderRadius: 3,
              p: 3,
              mb: 4,
              textAlign: "center",
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#64748b",
                fontWeight: 600,
                letterSpacing: 1,
              }}
            >
              Selected Workspace
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mt: 1,
                mb: 1,
              }}
            >
              {selectedLibraryForNextRound}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Round {nextRoundNumber}
              {t(`nextRound.modal.selectedWorkspaceDesc`)}
            </Typography>
          </Box>

          {/* Round N 필요성 섹션 */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1e293b",
                fontWeight: 700,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                fontSize: "1.1rem",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 5,
                  height: 28,
                  bgcolor: "#2f5596",
                  borderRadius: 1,
                }}
              />
              {t(`nextRound.modal.whyNeeded.title`)}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#475569",
                lineHeight: 1.8,
                mb: 2.5,
              }}
            >
              {t(`nextRound.modal.whyNeeded.description`)}
            </Typography>

            <Box
              sx={{
                bgcolor: "#f8fafc",
                borderLeft: "4px solid #cbd5e1",
                borderRadius: "0 8px 8px 0",
                p: 2.5,
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {Array.isArray(whyNeedNextRound) &&
                  whyNeedNextRound.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#64748b",
                          mt: 0.8,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#475569",
                          lineHeight: 1.6,
                          fontSize: "0.9rem",
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: "#eff6ff",
                border: "2px solid #bfdbfe",
                borderRadius: 2,
                p: 2.5,
                mb: 4,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#2f5596",
                  lineHeight: 1.8,
                  fontWeight: 500,
                }}
              >
                {t(`nextRound.modal.whyNeeded.conclusion`)}{" "}
                <Box
                  component="span"
                  sx={{ color: "#1e3a8a", fontWeight: 700 }}
                >
                  {t(`nextRound.modal.whyNeeded.conclusionBold`)}
                </Box>
                {t(`nextRound.modal.whyNeeded.conclusionEnd`)}
              </Typography>
            </Box>
          </Box>

          {/* Round 2 실행 조건 섹션 */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1e293b",
                fontWeight: 700,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                fontSize: "1.1rem",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 5,
                  height: 28,
                  bgcolor: "#2f5596",
                  borderRadius: 1,
                }}
              />
              {t(`nextRound.modal.conditions.title`)}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#475569",
                lineHeight: 1.8,
                mb: 2.5,
              }}
            >
              {t(`nextRound.modal.conditions.description`)}
            </Typography>

            <Box sx={{ mb: 3 }}>
              {Array.isArray(list) &&
                list.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "#f0fdf4",
                      border: "1.5px solid #bbf7d0",
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: "#dcfce7",
                        borderColor: "#86efac",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: "#10b981",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                        fontWeight: 700,
                        flexShrink: 0,
                        mt: 0.2,
                      }}
                    >
                      ✓
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#065f46",
                        lineHeight: 1.7,
                        flex: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
            </Box>

            <Box
              sx={{
                bgcolor: "#fffbeb",
                border: "2px solid #fde68a",
                borderRadius: 2,
                p: 2.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#92400e",
                  fontWeight: 700,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "0.95rem",
                }}
              >
                <Box component="span" sx={{ fontSize: "1.2rem" }}>
                  ⏱️
                </Box>
                {t(`nextRound.modal.conditions.timeRequired`)}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#78716c",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    {t(`nextRound.modal.conditions.labValidation`)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#44403c",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  >
                    {t(`nextRound.modal.conditions.labValidationTime`)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#78716c",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    {t(`nextRound.modal.conditions.pipelineExecution`)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#44403c",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  >
                    {t(`nextRound.modal.conditions.pipelineTime`)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 반복 횟수 테이블 섹션 */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "#1e293b",
                fontWeight: 700,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                fontSize: "1.1rem",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 5,
                  height: 28,
                  bgcolor: "#2f5596",
                  borderRadius: 1,
                }}
              />
              {t(`nextRound.modal.iteration.title`)}
            </Typography>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#334155",
                        borderBottom: "2px solid #e2e8f0",
                        fontSize: "0.85rem",
                        py: 1.5,
                      }}
                    >
                      Round
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#334155",
                        borderBottom: "2px solid #e2e8f0",
                        fontSize: "0.85rem",
                        py: 1.5,
                      }}
                    >
                      {t(`nextRound.modal.iteration.table.compoundCount`)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#334155",
                        borderBottom: "2px solid #e2e8f0",
                        fontSize: "0.85rem",
                        py: 1.5,
                      }}
                    >
                      {t(`nextRound.modal.iteration.table.stopCondition`)}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    {
                      round: "Round 1",
                      count: t(`nextRound.modal.iteration.table.count1`),
                      condition: "-",
                      highlight: false,
                    },
                    {
                      round: "Round 2",
                      count: t(`nextRound.modal.iteration.table.count2`),
                      condition: t(
                        `nextRound.modal.iteration.table.condition2`
                      ),
                      highlight: nextRoundNumber === 2,
                    },
                    {
                      round: "Round 3",
                      count: t(`nextRound.modal.iteration.table.count3`),
                      condition: t(
                        `nextRound.modal.iteration.table.condition3`
                      ),
                      highlight: nextRoundNumber === 3,
                    },
                    {
                      round: "Round 4+",
                      count: t(`nextRound.modal.iteration.table.count4`),
                      condition: t(
                        `nextRound.modal.iteration.table.condition4`
                      ),
                      highlight: nextRoundNumber >= 4,
                    },
                  ].map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        bgcolor: row.highlight ? "#fef3c7" : "white",
                        "&:hover": {
                          bgcolor: row.highlight ? "#fde68a" : "#fafafa",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: row.highlight ? 700 : 600,
                          color: row.highlight ? "#92400e" : "#475569",
                          fontSize: "0.85rem",
                          py: 1.5,
                        }}
                      >
                        {row.round}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.highlight ? "#78350f" : "#64748b",
                          fontSize: "0.85rem",
                          py: 1.5,
                        }}
                      >
                        {row.count}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.highlight ? "#78350f" : "#64748b",
                          fontSize: "0.85rem",
                          py: 1.5,
                        }}
                      >
                        {row.condition}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        {/* 액션 버튼 */}
        <DialogActions
          sx={{
            padding: "24px 40px 32px 40px",
            gap: 2,
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#f8fafc",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: "8px",
              padding: "12px 32px",
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#cbd5e1",
              color: "#475569",
              "&:hover": {
                borderColor: "#94a3b8",
                bgcolor: "#f1f5f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNextRound}
            sx={{
              background:
                "linear-gradient(135deg,rgb(47, 85, 150) 0%,rgb(47, 85, 150) 100%)",
              borderRadius: "12px",
              padding: "12px 32px",
              textTransform: "none",
              fontWeight: 600,
              color: "white",
              boxShadow: "0 4px 12px rgba(147, 178, 246, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
                boxShadow: "0 6px 16px rgba(37, 99, 235, 0.4)",
              },
            }}
          >
            Create Round {nextRoundNumber}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NextRoundMoveModal;
